import { getAddress, isAddress } from "ethers";
import type { AppConfig, ContractAddresses } from "./types";

const DEFAULT_RPC = "https://v2-mainnet-rpc.l1x.foundation/";
const DEFAULT_FALLBACK_RPC = "https://v2-mainnet-zeta-rpc.l1x.foundation/";
const DEFAULT_EXPLORER = "https://explorer.l1xapp.com";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function normaliseKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function addressIn(value: unknown): string {
  if (typeof value === "string" && isAddress(value)) return getAddress(value);
  const object = asRecord(value);
  for (const key of ["address", "contractAddress", "deploymentAddress"]) {
    const candidate = object[key];
    if (typeof candidate === "string" && isAddress(candidate)) return getAddress(candidate);
  }
  return "";
}

function explicitAddress(value: unknown, aliases: string[]): string {
  const wanted = aliases.map(normaliseKey);
  const queue: unknown[] = [value];
  while (queue.length > 0) {
    const current = asRecord(queue.shift());
    for (const [key, child] of Object.entries(current)) {
      if (wanted.includes(normaliseKey(key))) {
        const candidate = addressIn(child);
        if (candidate) return candidate;
      }
    }
    for (const child of Object.values(current)) {
      if (child !== null && typeof child === "object") queue.push(child);
    }
  }
  return "";
}

function findAddress(value: unknown, aliases: string[]): string {
  const wanted = aliases.map(normaliseKey);
  const queue: unknown[] = [value];

  while (queue.length > 0) {
    const current = asRecord(queue.shift());
    for (const alias of wanted) {
      for (const [key, child] of Object.entries(current)) {
        if (normaliseKey(key) === alias) {
          const candidate = addressIn(child);
          if (candidate) return candidate;
        }
      }
    }
    for (const child of Object.values(current)) {
      if (child !== null && typeof child === "object") queue.push(child);
    }
  }
  return "";
}

function deploymentContracts(value: unknown): ContractAddresses {
  const root = asRecord(value);
  if (typeof root.status === "string" && root.status !== "deployed-verified") {
    return {
      safeSingleton: "",
      safeProxyFactory: "",
      compatibilityFallbackHandler: "",
      multiSend: "",
      multiSendCallOnly: "",
    };
  }
  // A deployment manifest may also contain canonical *expected* addresses. Those are not
  // proof of deployment and must never become active configuration. When a contracts map
  // exists, search only that map.
  const contracts = Object.keys(asRecord(root.contracts)).length ? root.contracts : value;
  const safeL2 = explicitAddress(contracts, ["SafeL2"]);
  return {
    // SafeL2 is the preferred singleton for an L2-style chain; fall back to Safe if a
    // custom deployment file only exposes that singleton.
    safeSingleton: safeL2 || findAddress(contracts, ["SafeSingleton", "Safe"]),
    safeProxyFactory: findAddress(contracts, ["SafeProxyFactory", "ProxyFactory"]),
    compatibilityFallbackHandler: findAddress(contracts, [
      "CompatibilityFallbackHandler",
      "FallbackHandler",
    ]),
    multiSend: findAddress(contracts, ["MultiSend"]),
    multiSendCallOnly: findAddress(contracts, ["MultiSendCallOnly"]),
  };
}

function cleanAddress(value: unknown): string {
  return typeof value === "string" && isAddress(value) ? getAddress(value) : "";
}

function first(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

async function runtimeConfig(): Promise<UnknownRecord> {
  try {
    const response = await fetch("/l1x-safe-config.json", { cache: "no-store" });
    if (!response.ok) return {};
    return asRecord(await response.json());
  } catch {
    return {};
  }
}

export async function loadConfig(): Promise<AppConfig> {
  const runtime = await runtimeConfig();
  const runtimeContracts = asRecord(runtime.contracts);
  const deployed = deploymentContracts(__L1X_DEPLOYMENT__);

  const contracts: ContractAddresses = {
    safeSingleton: cleanAddress(
      first(
        runtimeContracts.safeSingleton,
        import.meta.env.VITE_SAFE_SINGLETON_ADDRESS,
        deployed.safeSingleton,
      ),
    ),
    safeProxyFactory: cleanAddress(
      first(
        runtimeContracts.safeProxyFactory,
        import.meta.env.VITE_SAFE_PROXY_FACTORY_ADDRESS,
        deployed.safeProxyFactory,
      ),
    ),
    compatibilityFallbackHandler: cleanAddress(
      first(
        runtimeContracts.compatibilityFallbackHandler,
        import.meta.env.VITE_SAFE_FALLBACK_HANDLER_ADDRESS,
        deployed.compatibilityFallbackHandler,
      ),
    ),
    multiSend: cleanAddress(
      first(
        runtimeContracts.multiSend,
        import.meta.env.VITE_SAFE_MULTISEND_ADDRESS,
        deployed.multiSend,
      ),
    ),
    multiSendCallOnly: cleanAddress(
      first(
        runtimeContracts.multiSendCallOnly,
        import.meta.env.VITE_SAFE_MULTISEND_CALL_ONLY_ADDRESS,
        deployed.multiSendCallOnly,
      ),
    ),
  };

  return {
    chainId: 1066,
    chainHex: "0x42a",
    chainName: "L1X Mainnet",
    nativeSymbol: "L1X",
    rpcUrl: first(runtime.rpcUrl, import.meta.env.VITE_L1X_RPC_URL, DEFAULT_RPC),
    fallbackRpcUrl: first(runtime.fallbackRpcUrl, DEFAULT_FALLBACK_RPC),
    explorerUrl: first(
      runtime.explorerUrl,
      import.meta.env.VITE_L1X_EXPLORER_URL,
      DEFAULT_EXPLORER,
    ).replace(/\/$/, ""),
    backendUrl: first(runtime.backendUrl, import.meta.env.VITE_BACKEND_URL, "/api").replace(
      /\/$/,
      "",
    ),
    walletConnectProjectId: first(import.meta.env.VITE_WALLETCONNECT_PROJECT_ID),
    wl1xTokenAddress: cleanAddress(
      first(
        runtime.wl1xTokenAddress,
        import.meta.env.VITE_WL1X_TOKEN_ADDRESS,
        __ROOT_WL1X_TOKEN_ADDRESS__,
      ),
    ),
    xusdTokenAddress: cleanAddress(
      first(runtime.xusdTokenAddress, import.meta.env.VITE_XUSD_TOKEN_ADDRESS),
    ),
    contracts,
  };
}

export function missingCreationContracts(config: AppConfig): string[] {
  const missing: string[] = [];
  if (!config.contracts.safeSingleton) missing.push("SafeL2 / Safe singleton");
  if (!config.contracts.safeProxyFactory) missing.push("SafeProxyFactory");
  if (!config.contracts.compatibilityFallbackHandler) {
    missing.push("CompatibilityFallbackHandler");
  }
  return missing;
}
